"use client";

import { jam } from "@jam.dev/sdk";
import { usePathname } from "next/navigation";
import { useEffect } from "react";
import {
  getDebugInfo,
  installBrowserFetchLogging,
  logPageView,
} from "@/lib/tracing/browser-logging";

/**
 * Browser observability bootstrap (GO-1580 / GO-2952): installs the global
 * `window.fetch` logging once and emits a `page_view` line on each SPA
 * navigation. Everything it does is mode-gated (see `lib/tracing/browser-logging`).
 */
export function BrowserObservability(): null {
  const pathname = usePathname();

  useEffect(() => {
    installBrowserFetchLogging();
    // GO-1580: attach the session/correlation ids to every Jam recording, so a
    // tester's Jam shows them in its metadata panel. The callback runs at
    // capture time, so the correlation id is always the most recent one.
    jam.metadata(() => {
      const { sessionId, correlationId, correlationIds, datadogUrl, datadogCorrelationUrls } =
        getDebugInfo();
      return {
        session_id: sessionId,
        correlation_id: correlationId ?? null, // most recent, at a glance
        correlation_ids: correlationIds, // every request this page made (batched → one per POST)
        datadog_url: datadogUrl, // the whole session, across every service
        datadog_correlation_urls: datadogCorrelationUrls, // one link per request
      };
    });
  }, []);

  useEffect(() => {
    if (pathname) logPageView(pathname);
  }, [pathname]);

  return null;
}
